import React, { useState } from "react"
import { Modal as AntModal } from "antd"
import type { ModalProps } from "./Modal.types"

/**
 * Modal component that wraps Ant Design Modal with render props pattern
 *
 * This component manages its own open/closed state and provides render prop
 * patterns for both triggering the modal and rendering content with access
 * to close functionality.
 *
 * @example
 * ```tsx
 * // Basic usage with renderButton
 * <Modal
 *   title="Create User"
 *   width={600}
 *   renderButton={(open) => <Button onClick={open}>Create User</Button>}
 * >
 *   {(close) => <UserForm onSuccess={() => close()} />}
 * </Modal>
 *
 * // Using content prop instead of children
 * <Modal
 *   title="Edit Profile"
 *   footer={null}
 *   renderButton={(open) => <Button type="primary" onClick={open}>Edit Profile</Button>}
 *   content={(close) => <ProfileForm onCancel={close} />}
 * />
 * ```
 */
const Modal: React.FC<ModalProps> = ({
  children,
  renderButton = () => <></>,
  visibleDefault = false,
  onClickCancel,
  onClickOk,
  content,
  ...antModalProps
}) => {
  const [visible, setVisible] = useState(visibleDefault)
  const open: () => void = () => setVisible(true)
  const close: () => void = () => setVisible(false)

  return (
    <React.Fragment>
      {renderButton(open)}
      <AntModal
        onCancel={() => onClickCancel?.(close) ?? close()}
        onOk={() => onClickOk?.(close) ?? close()}
        open={visible}
        {...antModalProps}
      >
        {content
          ? content(close)
          : typeof children === "function"
            ? (children as (_close: () => void) => React.ReactNode)(close)
            : children}
      </AntModal>
    </React.Fragment>
  )
}

export { Modal }
